import { useForm } from 'react-hook-form';
import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { find, filter } from 'lodash';
import { useAuth } from '../../auth';
import storage from '../../../lib/storage';
import { Listbox } from '@headlessui/react';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import Modal from './modal';
import { ErrorBanner, Button, ButtonLink, cn, inputClasses, dropdownItemClasses, dropdownItemActiveClasses, dropdownItemSelectedClasses, PortaledListboxOptions } from '../../../lib/ui';
import toast from 'react-hot-toast';
import {
  trackProductEvent,
  trackLimitHit,
  trackUpgradeCtaClicked,
  plansUrlWithUpgradeSource,
} from '../../analytics';
import { trackOneSignalCustomEvent } from '../../onesignal-events';
import {
  LivePreviewsUpsell,
  LIVE_PREVIEWS_UPSELL_STORAGE_KEY,
} from '../live-previews-upsell';

const isServer = typeof window === 'undefined';
const SEND_PREVIEW = gql`
  mutation SendPreview(
    $id: ID!
    $rawTemplate: String!
    $css: String
    $subject: String!
    $email: String!
    $recipientId: String!
  ) {
    sendPreviewTemplate(
      input: {
        id: $id
        rawTemplate: $rawTemplate
        css: $css
        subject: $subject
        email: $email
        recipientId: $recipientId
      }
    )
  }
`;

const subjOpts = {
  required: 'An email subject is required.',
};

type Inputs = {
  subject: string;
};

const defaultValues = {
  subject: 'Test HTML Email',
};

const LIST_RECIPIENTS = gql`
  query ListRecipients($accountId: ID) {
    listRecipients(accountId: $accountId) {
      data {
        id
        email
        status
        verifiedAt
        createdAt
        updatedAt
      }
    }
  }
`;

const RecipientDropdown = ({ items, isOpen, onChange, value, loading = false }) => {
  const [selectedItem, setItem] = useState(value ?? null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { user } = useAuth();
  const wrappedSetItem = useCallback((val) => {
    storage.set('postdrop.lastUsed.recipient', val);
    setItem(val);
    onChange(val);
  }, [onChange]);
  useEffect(() => {
    setItem(value ?? null);
  }, [value]);
  useEffect(() => {
    // if selectedItem is not in
    if (!isServer && !loading && isOpen && !selectedItem && items.length > 0) {
      const email = user?.attributes?.email;
      const lastUsedRecipient = storage.get('postdrop.lastUsed.recipient');
      if (
        lastUsedRecipient &&
        items.some((i) => i?.email === lastUsedRecipient)
      ) {
        wrappedSetItem(lastUsedRecipient);
      } else if (email && items.some((i) => i?.email === email)) {
        wrappedSetItem(email);
      } else {
        wrappedSetItem(items[0]?.email);
      }
    }
  }, [loading, wrappedSetItem, items, user, isOpen, selectedItem]);
  return (
    <Listbox
      as="div"
      className="space-y-1"
      disabled={loading}
      value={selectedItem}
      onChange={wrappedSetItem}>
      {({ open }) => (
        <>
          <div className="relative">
            <span className="inline-block w-full rounded-control shadow-input">
              <Listbox.Button
                ref={buttonRef}
                className={cn(inputClasses, "cursor-default relative pl-3 pr-10 py-2 text-left")}
              >
                <span className="block truncate">
                  {loading ? 'loading...' : selectedItem || 'Select item...'}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg
                    className="h-5 w-5 text-neutral-400"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor">
                    <path
                      d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Listbox.Button>
            </span>

            <PortaledListboxOptions open={open} buttonRef={buttonRef}>
              {!loading &&
                items.map((recipient) => (
                  <Listbox.Option key={recipient.id} value={recipient.email}>
                    {({ selected, active }) => (
                      <div
                        className={cn(
                          dropdownItemClasses,
                          'relative cursor-default select-none pl-8 pr-2',
                          active && dropdownItemActiveClasses,
                          selected && !active && dropdownItemSelectedClasses
                        )}
                      >
                        <span
                          className={cn(
                            'block truncate',
                            selected ? 'font-medium' : 'font-normal'
                          )}
                        >
                          {recipient.email}
                        </span>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-blue">
                            <svg
                              className="h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        )}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
            </PortaledListboxOptions>
          </div>
        </>
      )}
    </Listbox>
  );
};

function SendPreviewModal({ toggle, isOpen, data, account, onOpenLivePreviews }) {
  const [formError, setFormError] = useState(null);
  const [dropdownItem, setDropdownItem] = useState(null);
  const [isUpsellDismissed, setIsUpsellDismissed] = useState(true);
  const {
    register,
    handleSubmit,
    errors,
    reset,
    // setValue,
    // getValues,
    formState: { isSubmitting },
  } = useForm<Inputs>({ defaultValues });
  const [sendPreviewTemplate] = useMutation(SEND_PREVIEW);
  const [
    listRecipients,
    { loading, data: result, startPolling, stopPolling },
  ] = useLazyQuery(LIST_RECIPIENTS, {
    ssr: false,
    fetchPolicy: 'cache-and-network',
  });
  const items = result?.listRecipients?.data || [];
  const filteredItems = filter(items, { status: 'VERIFIED' });
  const isLoading = isServer || (loading && !data?.listRecipients?.data);
  const selectedRecipient = find(filteredItems, { email: dropdownItem });
  const recipientId = selectedRecipient?.id || null;
  const plan = account?.plan || 'FREE';
  const isFreePlan =
    account?.maxLivePreviewCredits === 0 || account?.plan?.toLowerCase() === 'free';
  const showLivePreviewsUpsell = isFreePlan && !isUpsellDismissed;
  const canSubmit = !isSubmitting && !isLoading && !!dropdownItem && !!recipientId;

  useEffect(() => {
    if (isOpen) {
      reset({
        subject: data?.subject?.trim() || defaultValues.subject,
      });
    }
  }, [data?.subject, isOpen, reset]);

  const upgradeButton = (
    <p className="text-sm my-4">
      <ButtonLink
        href={plansUrlWithUpgradeSource('send_preview_modal', 'send_test_daily')}
        target="_blank"
        onClick={() =>
          trackUpgradeCtaClicked({
            source: 'send_preview_modal',
            limit_type: 'send_test_daily',
            plan,
          })
        }
      >
        Upgrade
      </ButtonLink>
    </p>
  );

  const onSubmit = async ({ subject }, e) => {
    e.preventDefault();
    setFormError(null);
    if (!dropdownItem || !recipientId) {
      setFormError('Select a verified test recipient before sending.');
      return;
    }
    try {
      const promise = sendPreviewTemplate({
        variables: {
          id: data?.id,
          email: dropdownItem,
          subject,
          css: data?.css,
          rawTemplate: data?.rawTemplate,
          recipientId,
        },
      });
      toast.promise(promise, {
        loading: 'Sending test email...',
        success: `Test email sent to ${dropdownItem}`,
        error: 'Error sending test email.',
      });
      await promise;
      trackProductEvent('Test Email Sent', { template_id: data?.id });
      void trackOneSignalCustomEvent('Test Email Sent', {
        template_id: data?.id,
      });
      toggle();
      reset();
      setDropdownItem(null);
    } catch (error) {
      const message = error.message || 'An unknown error occurred on login.';
      setFormError(message);
      if (
        message.toLowerCase().includes('send limit') ||
        message.toLowerCase().includes('please upgrade')
      ) {
        trackLimitHit({
          limit_type: 'send_test_daily',
          plan,
          source: 'send_preview_modal',
        });
      }
    }
  };
  const dismissLivePreviewsUpsell = () => {
    trackProductEvent('Send Test Upsell Dismissed', {
      template_id: data?.id,
      cta: 'maybe_later',
    });
    storage.set(LIVE_PREVIEWS_UPSELL_STORAGE_KEY, 'dismissed');
    setIsUpsellDismissed(true);
  };
  const handleTryLivePreviews = () => {
    trackProductEvent('Send Test Upsell Clicked', {
      template_id: data?.id,
      cta: 'try_it_now',
    });
    toggle();
    if (onOpenLivePreviews) {
      window.setTimeout(() => {
        onOpenLivePreviews();
      }, 0);
    }
  };
  useEffect(() => {
    // fetch initial data
    if (isOpen && !stopPolling && !startPolling) {
      listRecipients();
    }
    // poll after initial fetch
    if (isOpen && stopPolling && startPolling) {
      startPolling(25 * 1000);
      return () => stopPolling();
    }
  }, [isOpen, startPolling, stopPolling, listRecipients]);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setIsUpsellDismissed(
      !isFreePlan || storage.get(LIVE_PREVIEWS_UPSELL_STORAGE_KEY) === 'dismissed'
    );
  }, [isOpen, isFreePlan]);
  const showUpgrade = formError?.includes('please upgrade');
  return (
    <Modal toggle={toggle} isOpen={isOpen} size="sm:max-w-lg" closeButton>
      <form onSubmit={handleSubmit(onSubmit)} className="text-left">
        <div className="sm:flex sm:items-start mb-4">
          <h3
            className="text-base font-semibold tracking-tight text-neutral-900"
            id="modal-headline">
            Send Test Email
          </h3>
        </div>
        {showLivePreviewsUpsell && (
          <LivePreviewsUpsell
            className="mt-4"
            onTryNow={handleTryLivePreviews}
            onDismiss={dismissLivePreviewsUpsell}
          />
        )}
        <div className="mt-4">
          <label
            htmlFor="recipient"
            className="block text-sm font-medium text-neutral-700">
            Select Test Recipient
          </label>
          <div className="mt-1">
            <RecipientDropdown
              loading={isLoading}
              items={filteredItems}
              isOpen={isOpen}
              value={dropdownItem}
              onChange={(val) => {
                setDropdownItem(val);
              }}
            />
            <p className="text-xs text-neutral-700 mt-1">
              Add a{' '}
              <Link
                href="/recipients"
                className="font-medium text-blue hover:text-blue"
                target="_blank">
                
                  test recipient
                
              </Link>
              .
            </p>
          </div>
          {/* {errors?.email && <ErrorBanner title={errors.email.message} />} */}
        </div>
        <div className="mt-4">
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-neutral-700">
            Email Subject
          </label>
          <div className="mt-1">
            <input
              name="subject"
              id="subject"
              ref={register(subjOpts)}
              className={inputClasses}
            />
          </div>
          {errors?.subject && <ErrorBanner title={errors.subject.message} />}
        </div>

        {formError && <ErrorBanner title={formError} children={showUpgrade ? upgradeButton : undefined} />}
        <div className="mt-6 mb-1 flex gap-2">
          <Button tag="button" type="submit" disabled={!canSubmit} id="btn-sendtest-modal">
            {isSubmitting ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default SendPreviewModal;
